import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../services/api";
import type { HomePageApiResponse, HomePageResponse } from "../types/homepage";

/**
 * Hook para obtener todos los datos del home en una sola llamada
 * 
 * ACTUALIZADO PARA BACKEND V2:
 * - Usa el endpoint optimizado /homepage
 * - Retorna navbar + hero carousel + standard carousels en 1 request
 * - Aprovecha el caché del backend (Redis)
 * - Mucho más rápido que 3 llamadas separadas
 * 
 * @returns {HomePageResponse} Datos completos de la home page
 */
export function useHomeData() {
  return useQuery<HomePageResponse>({
    queryKey: ["home", "data"],
    queryFn: async () => {
      // Nueva llamada: 1 endpoint en lugar de 3
      const response = await apiClient.get<HomePageApiResponse>("/homepage");
      
      // 🏠 LOG: Data CRUDA del backend (sin transformaciones) - usando JSON para ver todas las propiedades
      console.log("🏠 [HOME DATA] Response completa (JSON):", JSON.parse(JSON.stringify(response)));
      console.log("🏠 [HOME DATA] Response.data (JSON):", JSON.parse(JSON.stringify(response.data)));
      if (response.data?.navbar?.catalogs) {
        console.log("🏠 [HOME DATA] Primer catálogo (JSON):", JSON.parse(JSON.stringify(response.data.navbar.catalogs[0])));
      }
      
      // La respuesta viene envuelta en { success: true, data: {...} }
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos (el backend cachea por 1 hora)
    gcTime: 1000 * 60 * 10, // 10 minutos (cacheTime está deprecado, usar gcTime)
  });
}

// ==========================================
// HOOK LEGACY (DEPRECADO)
// Se mantiene temporalmente para compatibilidad
// ==========================================

/**
 * @deprecated Use useHomeData() instead
 * Este hook hace 3 llamadas separadas (MongoDB backend)
 */
export function useHomeDataLegacy() {
  return useQuery({
    queryKey: ["home", "data", "legacy"],
    queryFn: async () => {
      const results = await Promise.allSettled([
        apiClient.get("/carousels/hero"),
        apiClient.get("/carousels/standard"),
        apiClient.get("/catalog"),
      ]);

      return {
        heroCarousel:
          results[0].status === "fulfilled" ? results[0].value : null,
        standardCarousels:
          results[1].status === "fulfilled" ? results[1].value : [],
        catalogs: results[2].status === "fulfilled" ? results[2].value : null,
        errors: {
          hero: results[0].status === "rejected" ? results[0].reason : null,
          standard: results[1].status === "rejected" ? results[1].reason : null,
          catalogs: results[2].status === "rejected" ? results[2].reason : null,
        },
      };
    },
  });
}

// ==========================================
// HOOK PARA INVALIDAR CACHE
// ==========================================

/**
 * Hook para invalidar el cache de homeData
 * 
 * USO:
 * - Llama al endpoint del backend para invalidar el cache de Redis
 * - También invalida el cache de React Query en el frontend
 * - Útil cuando se necesite forzar una actualización inmediata
 * 
 * @returns {UseMutationResult} Mutación para invalidar cache
 */
export function useInvalidateHomeCache() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<{ success: boolean; message?: string }> => {
      // DELETE /api/v1/homepage/cache
      // Requiere autenticación (Bearer Token)
      const response = await apiClient.delete<{ success: boolean; message?: string }>(
        "/homepage/cache"
      );
      return response;
    },
    onSuccess: () => {
      // Invalidar cache de React Query en el frontend
      queryClient.invalidateQueries({ queryKey: ["home", "data"] });
      // También invalidar cache de navbar si existe
      queryClient.invalidateQueries({ queryKey: ["navbar"] });
    },
  });
}
